/**
 * AuthGate — route guard keyed on /api/auth/me + /api/staff-auth/me (ADR-003 D8).
 *
 * Status map → render target:
 *   - unknown / hydrating            → loading (probes in flight)
 *   - customerGatewayAvailable=unknown → loading (capability probe still
 *                                     resolving; the LoginPage cannot
 *                                     render without knowing whether to
 *                                     show the Customer tab)
 *   - anonymous / failed            → LoginPage (initial state or rejected attempt)
 *   - authenticating                → LoginPage (user is in the login flow;
 *                                     the LoginPage owns the credentials/OTP step
 *                                     via authStore.employeeStep — rendering the
 *                                     loading frame here would flash a blank
 *                                     screen during the employee OTP step).
 *   - awaiting_otp                  → LoginPage (credentials accepted, waiting
 *                                     on the user to enter the OTP — the
 *                                     OTP form is interactive, no network in
 *                                     flight, so this is NOT a loading state).
 *   - authenticated                 → dashboard (children)
 *
 * Why a single mounted hook drives everything: avoids race conditions between
 * the hydrate promise and the protected route's data loads — only after the
 * session is verified do the data stores start fetching.
 *
 * SIH-17 / DEF-B4-1: capability resolution is part of the loading gate. The
 * LoginPage must never render with capability='unknown' — it would have to
 * guess whether to show the Customer tab, and a wrong guess is the exact
 * bug this ADR closes (a false Customer affordance on a gateway-less host).
 */

import { useEffect, type ReactNode } from 'react'
import { useAuthStore } from '@stores/authStore'
import { LoginPage } from './LoginPage'

export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const status = useAuthStore((s) => s.status)
  const customerGatewayAvailable = useAuthStore((s) => s.customerGatewayAvailable)
  const hydrate = useAuthStore((s) => s.hydrate)

  useEffect(() => {
    // Abort in-flight hydrate on unmount; remount is the only path back to
    // 'unknown' after a successful hydrate (e.g. SPA page reload).
    const controller = new AbortController()
    void hydrate(controller.signal)
    return () => {
      controller.abort()
    }
  }, [hydrate])

  const sessionResolved = status !== 'unknown' && status !== 'hydrating'
  const capabilityResolved = customerGatewayAvailable !== 'unknown'

  if (status === 'authenticated' && capabilityResolved) {
    return <>{children}</>
  }

  if (sessionResolved && capabilityResolved) {
    // status is anonymous / failed / authenticating / awaiting_otp — all
    // pre-render the LoginPage; the page manages which sub-form is visible.
    return <LoginPage />
  }

  // Either the session probe is in flight OR the capability probe is in
  // flight OR both — render the loading frame. Never the login page, never
  // a protected screen, until both probes have settled.
  return (
    <div className="auth-gate-loading" data-testid="auth-gate-loading">
      <p>Checking your session…</p>
    </div>
  )
}
